import {
  AgentTypingRow,
  ChatAgentAvatar,
  ChatBubbleFrame,
  ChatComposer,
  ChatHeader,
  Flex,
  IconButton,
  PoweredByHubert,
  Progress,
  Text,
  Theme,
} from '@hubertai/hubert-component-library';
import { brandStyle, resolveLogo } from '@hubertai/hubert-component-library/theme';
import { MoreVertical } from 'lucide-react';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { ThemeDto } from '../api/themes.api';
import { defaultBrandingPrimaryColor } from 'types/branding.types';

// The candidate chat's surfaces, as its shell sets them on its theme.
const chatSurfaces = {
  '--color-panel': 'var(--gray-2)',
  '--color-surface': 'var(--gray-1)',
  '--color-background': 'var(--gray-3)',
  // The manager's own theme rounds Tailwind's corners from a 10px radius; the chat keeps Tailwind's 8px.
  '--radius': '0.5rem',
} as CSSProperties;

const sentAt = new Date(2026, 0, 1, 9, 41);

const messages = (agentName: string) =>
  [
    { role: 'agent', text: 'Hello,' },
    { role: 'agent', text: `My name is ${agentName} and I'm a virtual recruiter.` },
    { role: 'agent', text: 'Shall we start?' },
    { role: 'user', text: 'Yes' },
    { role: 'agent', text: "Then let's get started" },
  ] as const;

const accessibility = { getAriaLabel: (agentName: string) => agentName };

/** The candidate chat under the theme being edited, drawn with the chat's own components. */
export const ChatPreview = ({ theme }: { theme: ThemeDto }) => {
  const agentName = theme.agentName || 'Hubert';
  const colorScheme = theme.colorScheme ?? 'light';
  const logo = resolveLogo({ colorScheme, logo: theme.logo });
  // Anything the editor does not offer is auto, as in the chat.
  const textColor = theme.textColor === 'light' || theme.textColor === 'dark' ? theme.textColor : 'auto';
  const [answer, setAnswer] = useState('Great!');
  const script = messages(agentName);

  return (
    <div className="h-full overflow-hidden rounded-4xl border border-gray-200 shadow-2xl">
      <Theme
        appearance={colorScheme}
        style={{
          ...brandStyle({ brandColor: theme.brandColor || defaultBrandingPrimaryColor, textColor }),
          ...chatSurfaces,
        }}
        className="flex h-full flex-col bg-(--color-background)"
      >
        {/* The chat's panel; "Powered by" sits below it, on the page, as in the chat. */}
        <Flex direction="column" flexGrow="1" minHeight="0" className="bg-(--color-panel) shadow-(--shadow-3)">
          <ChatHeader
            flexShrink="0"
            logo={{ src: logo, alt: 'Logo' }}
            agent={{ name: agentName, status: 'online', accessibility, title: agentName, subtitle: 'Online' }}
            actions={
              // The chat's menu, drawn only: the preview has nothing to reset.
              <IconButton type="button" variant="ghost" color="gray" highContrast tabIndex={-1} aria-hidden>
                <MoreVertical />
              </IconButton>
            }
            progress={<Progress value={33} size="1" variant="soft" radius="none" aria-label="Progress" />}
          />

          <Flex direction="column" flexGrow="1" minHeight="0" overflowY="auto" pb="4">
            {script.map((message, index) => (
              <Flex
                key={message.text}
                gap="2"
                mt={index === 0 ? '4' : '1'}
                mb="1"
                minWidth="0"
                direction={message.role === 'user' ? 'row-reverse' : 'row'}
                align="end"
                className="px-4"
              >
                {message.role === 'agent' && (
                  // One avatar per run of agent messages, by its last, as in the chat. The typing row
                  // below carries its own, so it continues the last run.
                  <ChatAgentAvatar
                    agentName={agentName}
                    className={(script[index + 1]?.role ?? 'agent') === 'agent' ? 'invisible' : undefined}
                  />
                )}
                <ChatBubbleFrame role={message.role} time={sentAt}>
                  <Text as="p" size="2">
                    {message.text}
                  </Text>
                </ChatBubbleFrame>
              </Flex>
            ))}
            <AgentTypingRow agentName={agentName} className="my-1 ps-4" />
          </Flex>

          <div className="px-4">
            <ChatComposer
              visible
              canSend={answer.trim() !== ''}
              sendLabel="Send"
              placeholder="Type your message..."
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              onSubmit={(event) => event.preventDefault()}
            />
          </div>
        </Flex>

        {theme.level === 'advanced' && (
          <Flex justify="center">
            <PoweredByHubert className="w-max" />
          </Flex>
        )}
      </Theme>
    </div>
  );
};
